var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
const FILES = {
    submarine: 'audio/loader/submarine-loop.mp3',
    startup: 'audio/loader/console-startup.mp3',
    hum: 'audio/room/console-hum.mp3',
    server: 'audio/room/server-loop.mp3',
    waves: 'audio/room/lake-waves-loop.mp3',
    click: 'audio/ui/click.mp3',
};
const VOLUME = { submarine: 0.6, startup: 0.7, hum: 0.2, server: 0.25, waves: 0.2, click: 0.6 };
class Soundtrack {
    constructor() {
        this.buffers = new Map();
        this.playing = new Map();
        this.muted = false;
        this.listeners = new Set();
        // @ts-ignore
        const Context = window.AudioContext || window.webkitAudioContext;
        this.context = new Context();
        this.master = this.context.createGain();
        this.master.connect(this.context.destination);
        const unlock = () => { if (this.context.state !== 'running')
            this.context.resume(); };
        for (const type of ['pointerdown', 'keydown', 'touchstart']) {
            window.addEventListener(type, unlock, { capture: true, passive: true });
        }
        window.addEventListener('pointerdown', () => this.click(), { capture: true, passive: true });
        window.addEventListener('message', (e) => {
            var _a, _b, _c;
            if (((_a = e.data) === null || _a === void 0 ? void 0 : _a.type) === 'mousedown' || ((_b = e.data) === null || _b === void 0 ? void 0 : _b.type) === 'keydown')
                unlock();
            if (((_c = e.data) === null || _c === void 0 ? void 0 : _c.type) === 'mousedown')
                this.click();
        });
        this.load('click');
        this.context.onstatechange = () => this.emit(); // e.g. sound becomes allowed on the first input
    }
    /** Mutes or unmutes everything (the sound switch). */
    setMuted(muted) {
        this.muted = muted;
        this.fade(this.master.gain, muted ? 0 : 1, 0.25);
        this.emit();
    }
    /** Calls `listener` whenever `running` or `muted` changes; returns the unsubscribe. */
    onChange(listener) {
        this.listeners.add(listener);
        return () => { this.listeners.delete(listener); };
    }
    emit() {
        this.listeners.forEach((l) => l());
    }
    /** True once the browser lets sound play (after the first click or key). */
    get running() {
        return this.context.state === 'running';
    }
    load(track) {
        if (!this.buffers.has(track)) {
            this.buffers.set(track, fetch(FILES[track])
                .then((r) => r.arrayBuffer())
                .then((data) => new Promise((resolve, reject) => this.context.decodeAudioData(data, resolve, reject))));
        }
        return this.buffers.get(track);
    }
    /** Starts a looping track, fading in over `fadeIn` seconds. */
    loop(track, fadeIn = 1) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.playing.has(track))
                return;
            const buffer = yield this.load(track);
            const [start, end] = edges(buffer);
            const p = this.start(track, buffer, 0);
            p.source.loop = true;
            p.source.loopStart = start;
            p.source.loopEnd = end;
            p.source.start(0, start);
            this.fade(p.gain.gain, VOLUME[track], fadeIn);
        });
    }
    /** Plays a track once. Skipped if sound can't play yet, so it never fires late on the first click. */
    once(track) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.running)
                return;
            const buffer = yield this.load(track);
            const p = this.start(track, buffer, VOLUME[track]);
            p.source.onended = () => { if (this.playing.get(track) === p)
                this.playing.delete(track); };
            p.source.start();
        });
    }
    /** The click tick: overlaps freely, so fast clicks each get their own. */
    click() {
        return __awaiter(this, void 0, void 0, function* () {
            const buffer = yield this.load('click');
            const source = this.context.createBufferSource();
            source.buffer = buffer;
            const gain = this.context.createGain();
            gain.gain.value = VOLUME.click;
            source.connect(gain).connect(this.master);
            source.start(0, edges(buffer)[0]); // skip any decoder padding: no delay
        });
    }
    /** Fades a track out over `fadeOut` seconds and stops it. */
    stop(track, fadeOut = 1) {
        const p = this.playing.get(track);
        if (!p)
            return;
        this.playing.delete(track);
        if (!this.running) {
            p.source.stop();
            return;
        }
        this.fade(p.gain.gain, 0, fadeOut);
        p.source.stop(this.context.currentTime + fadeOut + 0.05);
    }
    start(track, buffer, volume) {
        const source = this.context.createBufferSource();
        source.buffer = buffer;
        const gain = this.context.createGain();
        gain.gain.value = volume;
        source.connect(gain).connect(this.master);
        const p = { source, gain };
        this.playing.set(track, p);
        return p;
    }
    fade(param, to, seconds) {
        const now = this.context.currentTime;
        param.cancelScheduledValues(now);
        param.setValueAtTime(param.value, now);
        param.linearRampToValueAtTime(to, now + Math.max(0.01, seconds));
    }
}
/** First and last sounding moments: MP3 decoders can add a little silence at the edges, which would gap a loop. */
function edges(buffer) {
    const data = buffer.getChannelData(0), n = data.length, limit = Math.min(n, Math.floor(buffer.sampleRate * 0.2));
    let a = 0, b = n - 1;
    while (a < limit && Math.abs(data[a]) < 1e-4)
        a++;
    while (b > n - limit && Math.abs(data[b]) < 1e-4)
        b--;
    if (a >= limit)
        a = 0;
    if (b <= n - limit)
        b = n - 1;
    return [a / buffer.sampleRate, (b + 1) / buffer.sampleRate];
}
export const soundtrack = new Soundtrack();
